import React, { useState, useRef, useEffect } from 'react';
import {
    Inbox,
    Settings,
    Search,
    ChevronDown,
    LogOut,
    UserPlus,
    Plus,
    Check,
    MoreHorizontal,
    ArrowRightLeft,
    Smile,
    ChevronRight,
    ChevronLeft,
    BellOff,
    MinusCircle,
    Users,
    Activity,
    Sun,
    Moon,
    Folder,
    Menu,
    X,
    Briefcase,
    Building2
} from 'lucide-react';
import { UserRole, PresenceStatus, Workspace } from '../types';
import { Modal, Button, Input } from './UIComponents';

interface LayoutProps {
    children: React.ReactNode;
    currentRole: UserRole;
    currentView: string;
    onNavigate: (view: string) => void;
    onSwitchRole: () => void;
    onLogout?: () => void;
    currentPresence?: PresenceStatus;
    onUpdatePresence?: (status: PresenceStatus) => void;
    onOpenSearch?: () => void;
    theme?: 'light' | 'dark';
    onToggleTheme?: () => void;
    userName?: string;
    userInitial?: string;
    // Workplace System Props
    workspaces?: Workspace[];
    currentWorkspaceId?: string;
    onSwitchWorkspace?: (id: string) => void;
    onCreateWorkspace?: (name: string) => void;
}

export const Layout: React.FC<LayoutProps> = ({
    children,
    currentRole,
    currentView,
    onNavigate,
    onSwitchRole,
    onLogout,
    currentPresence = PresenceStatus.ACTIVE,
    onUpdatePresence,
    onOpenSearch,
    theme = 'dark',
    onToggleTheme,
    userName,
    userInitial,
    workspaces = [],
    currentWorkspaceId,
    onSwitchWorkspace,
    onCreateWorkspace
}) => {
    const isProvider = currentRole === UserRole.PROVIDER;
    const [isWorkspaceMenuOpen, setIsWorkspaceMenuOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [menuView, setMenuView] = useState<'main' | 'status' | 'pause'>('main');
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false);
    const [newWorkspaceName, setNewWorkspaceName] = useState('');
    const [notificationPauseText, setNotificationPauseText] = useState<string | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const workspaceRef = useRef<HTMLDivElement>(null);
    const userMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (workspaceRef.current && !workspaceRef.current.contains(event.target as Node)) {
                setIsWorkspaceMenuOpen(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setIsUserMenuOpen(false);
                setMenuView('main');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close mobile menu on navigate
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [currentView]);

    const handleCreateWorkspaceSubmit = () => {
        if (onCreateWorkspace && newWorkspaceName) {
            onCreateWorkspace(newWorkspaceName);
            setNewWorkspaceName('');
            setIsCreateWorkspaceModalOpen(false);
            setIsWorkspaceMenuOpen(false);
        }
    };

    const NavItem = ({ view, icon: Icon, label, count, id }: { view: string, icon: any, label: string, count?: number, id?: string }) => {
        const isActive = currentView === view;
        return (
            <button
                id={id}
                onClick={() => onNavigate(view)}
                className={`w-full flex items-center justify-between px-3 py-1.5 mb-1 rounded-md transition-all group ${isActive
                    ? 'bg-zinc-200 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-800'
                    : 'text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-900 hover:text-zinc-700 dark:hover:text-zinc-300 border border-transparent'
                    }`}
            >
                <div className="flex items-center gap-3">
                    <Icon size={16} strokeWidth={1.5} className={isActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-500 group-hover:text-zinc-600 dark:group-hover:text-zinc-400'} />
                    <span className="text-[13px] font-medium">{label}</span>
                </div>
                {count !== undefined && count > 0 && (
                    <span className="text-[11px] text-zinc-600 dark:text-zinc-600 font-medium group-hover:text-zinc-500">{count}</span>
                )}
            </button>
        );
    };

    const getPresenceColor = (status: PresenceStatus) => {
        switch (status) {
            case PresenceStatus.ACTIVE: return 'bg-emerald-500 border-white dark:border-black';
            case PresenceStatus.FOCUSED: return 'bg-blue-500 border-white dark:border-black';
            case PresenceStatus.AWAY: return 'bg-amber-500 border-white dark:border-black';
            case PresenceStatus.OFFLINE: return 'bg-zinc-400 dark:bg-zinc-600 border-white dark:border-black';
        }
    };

    const activeWorkspace = workspaces.find(w => w.id === currentWorkspaceId);
    const currentWorkspaceName = activeWorkspace ? activeWorkspace.name : (isProvider ? 'My Workspace' : 'Receiver Workspace');
    const workspaceInitial = activeWorkspace ? activeWorkspace.initials : (isProvider ? 'W' : 'R');

    // Determine display values
    const displayUserName = userName || 'You';
    const displayInitial = userInitial || (displayUserName ? displayUserName.charAt(0).toUpperCase() : 'Y');

    return (
        <div className="flex h-screen overflow-hidden bg-zinc-50 dark:bg-black transition-colors duration-200">

            {/* Mobile Header */}
            <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-900 flex items-center px-4 z-30 justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <button onClick={() => setIsMobileMenuOpen(true)} className="text-zinc-500 p-1 -ml-1 hover:text-zinc-900 dark:hover:text-zinc-100">
                        <Menu size={20} />
                    </button>
                    <span className="font-semibold text-sm text-zinc-900 dark:text-zinc-100 tracking-tight">Verline</span>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={onOpenSearch} className="text-zinc-500">
                        <Search size={18} />
                    </button>
                    <div className="w-6 h-6 rounded bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center text-[10px] text-zinc-600 dark:text-zinc-300 font-medium">
                        {displayInitial}
                    </div>
                </div>
            </div>

            {/* Mobile Sidebar Overlay */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-[240px] bg-zinc-50 dark:bg-zinc-950 border-r border-zinc-200 dark:border-zinc-900 flex flex-col shrink-0 transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="px-3 pt-3 pb-2 relative flex items-center justify-between" ref={workspaceRef}>
                    <button
                        id="workspace-trigger"
                        onClick={() => setIsWorkspaceMenuOpen(!isWorkspaceMenuOpen)}
                        className="flex-1 flex items-center gap-2.5 px-2 py-1.5 hover:bg-zinc-200 dark:hover:bg-zinc-900 rounded-md transition-colors text-left group border border-transparent hover:border-zinc-300 dark:hover:border-zinc-800"
                    >
                        <div className="w-5 h-5 bg-white dark:bg-zinc-900 rounded flex items-center justify-center text-zinc-700 dark:text-zinc-200 text-[10px] font-bold border border-zinc-200 dark:border-zinc-800 shrink-0 shadow-sm dark:shadow-none">
                            {workspaceInitial}
                        </div>
                        <div className="flex-1 truncate">
                            <span className="text-[13px] font-medium text-zinc-800 dark:text-zinc-200 truncate block tracking-tight">
                                {currentWorkspaceName}
                            </span>
                        </div>
                        <ChevronDown size={14} className="text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 dark:group-hover:text-zinc-400" />
                    </button>

                    <button onClick={() => setIsMobileMenuOpen(false)} className="md:hidden p-2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200">
                        <X size={16} />
                    </button>

                    {isWorkspaceMenuOpen && (
                        <div className="absolute top-12 left-2 w-[240px] bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-xl dark:shadow-2xl z-50 animate-fade-in flex flex-col overflow-hidden">
                            <div className="p-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                                {/* Workspace List */}
                                <div className="px-3 py-1.5 text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">Workplaces</div>
                                {workspaces.map(ws => (
                                    <button
                                        key={ws.id}
                                        onClick={() => { if (onSwitchWorkspace) onSwitchWorkspace(ws.id); setIsWorkspaceMenuOpen(false); }}
                                        className="w-full text-left px-3 py-2 text-[13px] hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-md transition-colors flex items-center justify-between group"
                                    >
                                        <span className={`${currentWorkspaceId === ws.id ? 'text-zinc-900 dark:text-zinc-100 font-medium' : 'text-zinc-600 dark:text-zinc-400'}`}>{ws.name}</span>
                                        {currentWorkspaceId === ws.id && <Check size={14} className="text-zinc-900 dark:text-zinc-100" />}
                                    </button>
                                ))}
                                <button
                                    onClick={() => { setIsCreateWorkspaceModalOpen(true); setIsWorkspaceMenuOpen(false); }}
                                    className="w-full text-left px-3 py-2 text-[13px] text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-md transition-colors flex items-center gap-2 mt-1"
                                >
                                    <Plus size={14} /> Create Workplace
                                </button>

                                <div className="h-px bg-zinc-100 dark:bg-zinc-900 my-1 mx-1"></div>

                                {/* View Options */}
                                <div className="px-3 py-1.5 text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">View Options</div>
                                <button onClick={() => { onSwitchRole(); setIsWorkspaceMenuOpen(false); }} className="w-full text-left px-3 py-2 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-md transition-colors flex items-center gap-3">
                                    <ArrowRightLeft size={14} className="text-zinc-400 dark:text-zinc-500" />
                                    <span>Switch to {isProvider ? 'Receiver' : 'Provider'} view</span>
                                </button>

                                <div className="h-px bg-zinc-100 dark:bg-zinc-900 my-1 mx-1"></div>

                                <button onClick={() => { onNavigate('settings'); setIsWorkspaceMenuOpen(false); }} className="w-full text-left px-3 py-2 text-[13px] text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-md transition-colors flex items-center gap-3">
                                    <Settings size={14} /> Settings
                                </button>
                                <button
                                    onClick={() => { if (onLogout) onLogout(); setIsWorkspaceMenuOpen(false); }}
                                    className="w-full text-left px-3 py-2 text-[13px] text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-md transition-colors flex items-center gap-3"
                                >
                                    <LogOut size={14} /> Log out
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto px-3 space-y-6 mt-2">
                    <div className="hidden md:block">
                        <button
                            onClick={onOpenSearch}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-900 rounded-md transition-colors text-[13px] group border border-transparent hover:border-zinc-300 dark:hover:border-zinc-800"
                        >
                            <Search size={14} strokeWidth={1.5} className="text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 dark:group-hover:text-zinc-400" />
                            <span>Search</span>
                            <span className="ml-auto text-[10px] text-zinc-400 dark:text-zinc-600 border border-zinc-200 dark:border-zinc-800 rounded px-1.5 bg-zinc-100 dark:bg-zinc-950">⌘K</span>
                        </button>
                    </div>

                    <div className="space-y-0.5">
                        <p className="px-3 py-2 text-[10px] font-medium text-zinc-500 dark:text-zinc-600 uppercase tracking-wider">Console</p>
                        <NavItem view="projects" icon={Folder} label="Projects" id="nav-item-projects" />
                        <NavItem view="inbox" icon={Inbox} label="Inbox" count={2} id="nav-item-inbox" />
                    </div>

                    <div className="space-y-0.5">
                        <p className="px-3 py-2 text-[10px] font-medium text-zinc-500 dark:text-zinc-600 uppercase tracking-wider">Network</p>
                        <NavItem view="people" icon={Users} label="People" id="nav-item-people" />
                    </div>
                </div>

                <div className="p-3 border-t border-zinc-200 dark:border-zinc-900 relative" ref={userMenuRef}>
                    <div
                        onClick={() => { setIsUserMenuOpen(!isUserMenuOpen); setMenuView('main'); }}
                        className={`flex items-center gap-2.5 px-2 py-2 mb-1 rounded-md cursor-pointer transition-colors group ${isUserMenuOpen ? 'bg-zinc-200 dark:bg-zinc-900' : 'hover:bg-zinc-200 dark:hover:bg-zinc-900'}`}
                    >
                        <div className="relative">
                            <div className="w-7 h-7 rounded bg-white dark:bg-zinc-900 flex items-center justify-center text-[11px] text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-800">
                                {displayInitial}
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-zinc-50 dark:border-zinc-950 ${getPresenceColor(currentPresence)}`}></div>
                        </div>
                        <div className="flex-1 truncate">
                            <span className="text-[13px] font-medium text-zinc-800 dark:text-zinc-200 block flex items-center gap-2">
                                {displayUserName}
                                {notificationPauseText && <BellOff size={10} className="text-zinc-400 dark:text-zinc-500" />}
                            </span>
                        </div>
                        <div className="text-zinc-400 dark:text-zinc-600 group-hover:text-zinc-600 dark:group-hover:text-zinc-400">
                            <MoreHorizontal size={14} />
                        </div>
                    </div>

                    {isUserMenuOpen && (
                        <div className="absolute bottom-14 left-2 w-[260px] bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl dark:shadow-2xl overflow-hidden animate-fade-in z-50 flex flex-col">
                            {menuView === 'main' && (
                                <>
                                    <div className="p-4 pb-3 flex items-center gap-3">
                                        <div className="w-10 h-10 rounded bg-zinc-100 dark:bg-zinc-900 flex items-center justify-center text-[14px] text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-800 shrink-0 font-medium">
                                            {displayInitial}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[14px] font-bold text-zinc-800 dark:text-zinc-200 truncate">{displayUserName}</div>
                                            <div className="flex items-center gap-1.5 text-[12px] text-zinc-500">
                                                <div className={`w-2 h-2 rounded-full ${getPresenceColor(currentPresence).split(' ')[0]}`}></div>
                                                <span>
                                                    {currentPresence === PresenceStatus.ACTIVE ? 'Active' :
                                                        currentPresence === PresenceStatus.AWAY ? 'Away' :
                                                            currentPresence === PresenceStatus.FOCUSED ? 'Do not disturb' : 'Offline'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="h-px bg-zinc-100 dark:bg-zinc-900 mx-0 my-1"></div>
                                    <div className="py-1">
                                        <button onClick={() => setMenuView('status')} className="w-full text-left px-4 py-1.5 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors flex justify-between items-center group">
                                            Set Status <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 text-zinc-400 dark:text-zinc-600" />
                                        </button>
                                        <button onClick={() => { setIsProfileModalOpen(true); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-1.5 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors">Profile</button>
                                        <button onClick={() => { onNavigate('settings'); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-1.5 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors">Settings</button>
                                        {onToggleTheme && (
                                            <button onClick={onToggleTheme} className="w-full text-left px-4 py-1.5 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors flex items-center justify-between">
                                                <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                                                {theme === 'dark' ? <Sun size={14} className="text-zinc-500" /> : <Moon size={14} className="text-zinc-500" />}
                                            </button>
                                        )}
                                    </div>
                                    <div className="h-px bg-zinc-100 dark:bg-zinc-900 mx-0 my-1"></div>
                                    <div className="py-1">
                                        <button onClick={() => { if (onLogout) onLogout(); }} className="w-full text-left px-4 py-1.5 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors">
                                            Sign out
                                        </button>
                                    </div>
                                </>
                            )}
                            {menuView === 'status' && (
                                <div className="animate-fade-in">
                                    <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200 dark:border-zinc-900 mb-1">
                                        <button onClick={() => setMenuView('main')} className="p-1 -ml-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300">
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="text-[13px] font-bold text-zinc-800 dark:text-zinc-200">Set Status</span>
                                    </div>
                                    <div className="py-1">
                                        {[
                                            { label: 'Active', val: PresenceStatus.ACTIVE, icon: <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> },
                                            { label: 'Away', val: PresenceStatus.AWAY, icon: <div className="w-2.5 h-2.5 rounded-full bg-amber-500" /> },
                                            { label: 'Do not disturb', val: PresenceStatus.FOCUSED, icon: <MinusCircle size={12} className="text-blue-500" /> },
                                            { label: 'Offline', val: PresenceStatus.OFFLINE, icon: <div className="w-2.5 h-2.5 rounded-full border border-zinc-400 dark:border-zinc-600" /> },
                                        ].map((item) => (
                                            <button
                                                key={item.label}
                                                onClick={() => { if (onUpdatePresence) onUpdatePresence(item.val); setIsUserMenuOpen(false); }}
                                                className="w-full flex items-center gap-3 px-4 py-2 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
                                            >
                                                <div className="w-4 flex justify-center">{item.icon}</div>
                                                {item.label}
                                                {currentPresence === item.val && <Check size={12} className="ml-auto text-emerald-500" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </aside>

            <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-white dark:bg-black relative transition-colors duration-200 pt-14 md:pt-0">
                {children}
            </main>

            {/* Edit Profile Modal */}
            <Modal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} title="Edit Profile">
                <div className="p-6">
                    <div className="flex items-start gap-6 mb-6">
                        <div className="w-24 h-24 bg-zinc-100 dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 flex items-center justify-center text-2xl text-zinc-600 dark:text-zinc-200 font-medium">
                            {displayInitial}
                        </div>
                        <div className="space-y-4 flex-1">
                            <div className="space-y-1">
                                <label className="text-[11px] font-medium text-zinc-500 uppercase">Full Name</label>
                                <Input defaultValue={displayUserName} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[11px] font-medium text-zinc-500 uppercase">Display Name</label>
                                <Input defaultValue={displayUserName.split(' ')[0]} />
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setIsProfileModalOpen(false)}>Cancel</Button>
                        <Button onClick={() => setIsProfileModalOpen(false)}>Save Changes</Button>
                    </div>
                </div>
            </Modal>

            {/* Create Workspace Modal */}
            <Modal isOpen={isCreateWorkspaceModalOpen} onClose={() => setIsCreateWorkspaceModalOpen(false)} title="Create Workplace">
                <div className="p-6">
                    <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-900 rounded-xl flex items-center justify-center mx-auto mb-6 text-zinc-500 dark:text-zinc-400">
                        <Building2 size={24} strokeWidth={1.5} />
                    </div>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-zinc-500 uppercase">Workplace Name</label>
                            <Input
                                autoFocus
                                placeholder="e.g. Acme Design, Personal Projects"
                                value={newWorkspaceName}
                                onChange={(e) => setNewWorkspaceName(e.target.value)}
                            />
                            <p className="text-[11px] text-zinc-500">This will be the name of your new team environment.</p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="ghost" onClick={() => setIsCreateWorkspaceModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateWorkspaceSubmit} disabled={!newWorkspaceName.trim()}>Create Workplace</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};